import {type ReactNode, useEffect, useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {NearbyOffersPanel} from "../../nearby_offers/NearbyOffersPanel";
import {NearbyOffersPanelPlaceholder} from "../../nearby_offers/NearbyOffersPanelPlaceholder";
import {Country} from "../../region/types/Country";
import {getRequestStateFromRtkQuery} from "../../store/utils/get_request_state_from_rtk_query";
import {neighborhoodOffersClickHit} from "../../tracking/algolytics_hits/neighborhood_offers_click_hit";
import {neighborhoodOffersViewHit} from "../../tracking/algolytics_hits/neighborhood_offers_view_hit";
import {AmplitudeViewItemListLocation, trackAmplitudeOfferViewItemList} from "../../tracking/amplitude/track_amplitude_offer_view_item_list";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {useUspListingTileExperiment} from "../hooks/use_usp_listing_tile_experiment";
import {NearbyOffersMoreCard} from "../list/components/NearbyOffersMoreCard";
import {hitGTMOfferViewItemList} from "../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {OfferType} from "../types/OfferType";
import {getDetailPageNearbyOffersPayload} from "../utils/get_detail_page_nearby_offers_payload";

interface IProps {
    viewType: ViewType;
    className?: string;
    viewSectionTracker?: ReactNode;
    offer: {
        id: number;
        type: OfferType;
        region: {
            country: Country;
            name_declension_where: string;
            slug: string;
            id: number;
            full_name: string;
            short_name_reverted: string;
        };
        vendor: {
            id: number;
            name: string;
            slug: string;
        };
    };
}

const NEARBY_OFFERS_WRAPPER_ID = "nearby-offers-wrapper";

export const DetailNearbyOffers = (props: IProps) => {
    const {offer, viewType, className} = props;

    const [shouldFetchNearbyOffers, setShouldFetchNearbyOffers] = useState(false);

    useElementWasVisible({
        ids: [NEARBY_OFFERS_WRAPPER_ID],
        onElementVisible: () => {
            setShouldFetchNearbyOffers(true);
        },
        once: true,
        threshold: 0.05
    });
    const {
        data: nearbyOffersResponse,
        isSuccess,
        isUninitialized,
        isLoading,
        isFetching,
        isError
    } = useGetOfferListQuery(getDetailPageNearbyOffersPayload(offer), {
        skip: !shouldFetchNearbyOffers
    });
    const nearbyOffers = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    const offerIds = useMemo(() => nearbyOffers?.map((offer) => offer.id) ?? [], [nearbyOffers]);
    const amplitudeLocation: AmplitudeViewItemListLocation = viewType === ViewType.PROPERTY ? "property_page" : "investment_page";

    const listingHeadline = "Oferty, które mogą Cię zainteresować";
    const listId = "oferty_ktore_moga_cie_zainteresowac";

    // OfferBox (rendered by NearbyOffersPanel) reads this same experiment hook to pick its layout —
    // read it here too so the tracked isUspVariant matches what's actually on screen.
    const {isUspVariant} = useUspListingTileExperiment();

    // Neighborhood offers hits
    useEffect(() => {
        if (isEmpty(offerIds)) return;

        neighborhoodOffersViewHit(viewType, offerIds);

        hitGTMOfferViewItemList({
            offers: nearbyOffers,
            listId,
            listName: listingHeadline
        });
    }, [offerIds]);

    useEffect(() => {
        if (nearbyOffers.length > 0) {
            trackAmplitudeOfferViewItemList({
                offers: nearbyOffers,
                itemListName: listingHeadline,
                location: amplitudeLocation,
                isUspVariant,
                addressContext: {}
            });
        }
    }, [nearbyOffers]);

    const onNeighborhoodLastClick = () => neighborhoodOffersClickHit(viewType, null);

    const abroadOffer = offer.region.country !== Country.POLAND;
    const {friendlySlug, countrySlug} = offerUrlBuilder({type: offer.type, region: offer.region.slug, country: offer.region.country});

    const url = abroadOffer
        ? rpAppLink.offer.listFriendlyAbroad({
              friendlySlug: friendlySlug,
              country: countrySlug,
              abroadRegionSlug: offer.region.slug
          })
        : rpAppLink.offer.listFriendly({friendlySlug: friendlySlug});

    //  Render
    if (isSuccess && nearbyOffers.length === 0) {
        return null;
    }

    return (
        <div className={cx(className, wrapStyle)} data-id={NEARBY_OFFERS_WRAPPER_ID} data-testid={OFFER_TEST_IDS.OFFER_VIEW.NEARBY_OFFERS_SECTION}>
            {isSuccess && nearbyOffers.length > 0 && props.viewSectionTracker}
            <WideContent>
                <Text variant="headline_4" as="h2" className={headingStyle}>
                    {listingHeadline}
                </Text>
                {!isSuccess ? (
                    <NearbyOffersPanelPlaceholder />
                ) : (
                    nearbyOffers.length >= 1 && (
                        <NearbyOffersPanel
                            offersNearby={nearbyOffersResponse.results}
                            trackingMeta={{
                                listId,
                                listName: listingHeadline,
                                source: "additional",
                                tileEvents: {
                                    imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                    buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                    linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                    logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                }
                            }}
                            lastElement={
                                <NearbyOffersMoreCard href={url} onClick={onNeighborhoodLastClick}>
                                    Zobacz wszystkie oferty <br />
                                    {abroadOffer ? "z zagranicy" : offer.region.name_declension_where}
                                </NearbyOffersMoreCard>
                            }
                            modalQuery={{
                                isInitialized: !isUninitialized,
                                isSuccess,
                                isLoading: isLoading || isFetching,
                                isError,
                                requestState: getRequestStateFromRtkQuery({isSuccess, isLoading, isFetching, isError}),
                                data: nearbyOffers,
                                count: nearbyOffers.length,
                                page: nearbyOffersResponse.page,
                                pageSize: nearbyOffersResponse.page_size
                            }}
                        />
                    )
                )}
            </WideContent>
        </div>
    );
};

const wrapStyle = css({
    pt: 3,

    md: {
        pt: 4
    }
});

const headingStyle = css({
    mb: "2.4rem",

    md: {
        mb: "3.6rem"
    }
});
import {type ReactNode, useEffect, useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {NearbyOffersPanel} from "../../nearby_offers/NearbyOffersPanel";
import {NearbyOffersPanelPlaceholder} from "../../nearby_offers/NearbyOffersPanelPlaceholder";
import {Country} from "../../region/types/Country";
import {getRequestStateFromRtkQuery} from "../../store/utils/get_request_state_from_rtk_query";
import {neighborhoodOffersClickHit} from "../../tracking/algolytics_hits/neighborhood_offers_click_hit";
import {neighborhoodOffersViewHit} from "../../tracking/algolytics_hits/neighborhood_offers_view_hit";
import {AmplitudeViewItemListLocation, trackAmplitudeOfferViewItemList} from "../../tracking/amplitude/track_amplitude_offer_view_item_list";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {useUspListingTileExperiment} from "../hooks/use_usp_listing_tile_experiment";
import {NearbyOffersMoreCard} from "../list/components/NearbyOffersMoreCard";
import {hitGTMOfferViewItemList} from "../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {OfferType} from "../types/OfferType";
import {getDetailPageNearbyOffersPayload} from "../utils/get_detail_page_nearby_offers_payload";

interface IProps {
    viewType: ViewType;
    className?: string;
    viewSectionTracker?: ReactNode;
    offer: {
        id: number;
        type: OfferType;
        region: {
            country: Country;
            name_declension_where: string;
            slug: string;
            id: number;
            full_name: string;
            short_name_reverted: string;
        };
        vendor: {
            id: number;
            name: string;
            slug: string;
        };
    };
}

const NEARBY_OFFERS_WRAPPER_ID = "nearby-offers-wrapper";

export const DetailNearbyOffers = (props: IProps) => {
    const {offer, viewType, className} = props;

    const [shouldFetchNearbyOffers, setShouldFetchNearbyOffers] = useState(false);

    useElementWasVisible({
        ids: [NEARBY_OFFERS_WRAPPER_ID],
        onElementVisible: () => {
            setShouldFetchNearbyOffers(true);
        },
        once: true,
        threshold: 0.05
    });
    const {
        data: nearbyOffersResponse,
        isSuccess,
        isUninitialized,
        isLoading,
        isFetching,
        isError
    } = useGetOfferListQuery(getDetailPageNearbyOffersPayload(offer), {
        skip: !shouldFetchNearbyOffers
    });
    const nearbyOffers = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    const offerIds = useMemo(() => nearbyOffers?.map((offer) => offer.id) ?? [], [nearbyOffers]);
    const amplitudeLocation: AmplitudeViewItemListLocation = viewType === ViewType.PROPERTY ? "property_page" : "investment_page";

    const listingHeadline = "Oferty, które mogą Cię zainteresować";
    const listId = "oferty_ktore_moga_cie_zainteresowac";

    // OfferBox (rendered by NearbyOffersPanel) reads this same experiment hook to pick its layout —
    // read it here too so the tracked isUspVariant matches what's actually on screen.
    const {isUspVariant} = useUspListingTileExperiment();

    // Neighborhood offers hits
    useEffect(() => {
        if (isEmpty(offerIds)) return;

        neighborhoodOffersViewHit(viewType, offerIds);

        hitGTMOfferViewItemList({
            offers: nearbyOffers,
            listId,
            listName: listingHeadline
        });
    }, [offerIds]);

    useEffect(() => {
        if (nearbyOffers.length > 0) {
            trackAmplitudeOfferViewItemList({
                offers: nearbyOffers,
                itemListName: listingHeadline,
                location: amplitudeLocation,
                isUspVariant,
                addressContext: {}
            });
        }
    }, [nearbyOffers]);

    const onNeighborhoodLastClick = () => neighborhoodOffersClickHit(viewType, null);

    const abroadOffer = offer.region.country !== Country.POLAND;
    const {friendlySlug, countrySlug} = offerUrlBuilder({type: offer.type, region: offer.region.slug, country: offer.region.country});

    const url = abroadOffer
        ? rpAppLink.offer.listFriendlyAbroad({
              friendlySlug: friendlySlug,
              country: countrySlug,
              abroadRegionSlug: offer.region.slug
          })
        : rpAppLink.offer.listFriendly({friendlySlug: friendlySlug});

    //  Render
    if (isSuccess && nearbyOffers.length === 0) {
        return null;
    }

    return (
        <div className={cx(className, wrapStyle)} data-id={NEARBY_OFFERS_WRAPPER_ID} data-testid={OFFER_TEST_IDS.OFFER_VIEW.NEARBY_OFFERS_SECTION}>
            {isSuccess && nearbyOffers.length > 0 && props.viewSectionTracker}
            <WideContent>
                <Text variant="headline_4" as="h2" className={headingStyle}>
                    {listingHeadline}
                </Text>
                {!isSuccess ? (
                    <NearbyOffersPanelPlaceholder />
                ) : (
                    nearbyOffers.length >= 1 && (
                        <NearbyOffersPanel
                            offersNearby={nearbyOffersResponse.results}
                            trackingMeta={{
                                listId,
                                listName: listingHeadline,
                                source: "additional",
                                tileEvents: {
                                    imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                    buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                    linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                    logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                }
                            }}
                            lastElement={
                                <NearbyOffersMoreCard href={url} onClick={onNeighborhoodLastClick}>
                                    Zobacz wszystkie oferty <br />
                                    {abroadOffer ? "z zagranicy" : offer.region.name_declension_where}
                                </NearbyOffersMoreCard>
                            }
                            modalQuery={{
                                isInitialized: !isUninitialized,
                                isSuccess,
                                isLoading: isLoading || isFetching,
                                isError,
                                requestState: getRequestStateFromRtkQuery({isSuccess, isLoading, isFetching, isError}),
                                data: nearbyOffers,
                                count: nearbyOffers.length,
                                page: nearbyOffersResponse.page,
                                pageSize: nearbyOffersResponse.page_size
                            }}
                        />
                    )
                )}
            </WideContent>
        </div>
    );
};

const wrapStyle = css({
    pt: 3,

    md: {
        pt: 4
    }
});

const headingStyle = css({
    mb: "2.4rem",

    md: {
        mb: "3.6rem"
    }
});
